import React, { useEffect, useRef, useState } from 'react';

function cameraErrorMessage(err) {
  const name = String(err?.name || '');
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
    return 'Camera access was denied. Allow Camera for Line Medic in iPhone Settings, then try again.';
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') return 'No usable camera was found on this device.';
  return err?.message || 'Unable to access the camera.';
}

function waitForVideo(ref) {
  return new Promise(resolve => {
    const check = () => {
      if (ref.current) resolve(ref.current);
      else requestAnimationFrame(check);
    };
    requestAnimationFrame(check);
  });
}

export function handoffTokenFromQr(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';

  try {
    const url = new URL(raw, window.location.origin);
    const pathMatch = url.pathname.match(/^\/handoff\/([A-Za-z0-9_-]+)\/?$/i);
    if (pathMatch?.[1]) return pathMatch[1];

    if (/^\/tracking\/?$/i.test(url.pathname)) {
      const compatibilityToken = String(url.searchParams.get('handoff') || '').trim();
      if (/^[A-Za-z0-9_-]+$/.test(compatibilityToken)) return compatibilityToken;
    }
  } catch (_) {}

  const directMatch = raw.match(/^\/?handoff\/([A-Za-z0-9_-]+)\/?$/i);
  return directMatch?.[1] || '';
}

function openHandoff(token) {
  const path = `/handoff/${encodeURIComponent(token)}`;
  window.history.pushState({}, '', path);
  window.dispatchEvent(new CustomEvent('smfd:native-route', { detail: { path } }));
}

export default function GearHandoffScanner() {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const frameRef = useRef(null);
  const zxingControlsRef = useRef(null);
  const handledRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [manual, setManual] = useState('');
  const [error, setError] = useState('');
  const [engine, setEngine] = useState('');

  const stopCamera = () => {
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    if (zxingControlsRef.current) {
      try { zxingControlsRef.current.stop(); } catch (_) {}
      zxingControlsRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setOpen(false);
    setEngine('');
  };

  useEffect(() => () => stopCamera(), []);

  const acceptValue = value => {
    if (handledRef.current) return false;
    const token = handoffTokenFromQr(value);
    if (!token) {
      setError('That QR is not a Line Medic gear handoff. Scan the handoff QR shown on the other member’s Transfer Gear screen.');
      return false;
    }

    handledRef.current = true;
    stopCamera();
    setError('');
    openHandoff(token);
    return true;
  };

  const startNativeCamera = async video => {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
      audio: false,
    });
    streamRef.current = stream;
    video.srcObject = stream;
    await video.play();

    const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
    setEngine('native');

    const scanFrame = async () => {
      try {
        if (videoRef.current?.readyState >= 2) {
          const codes = await detector.detect(videoRef.current);
          if (codes?.[0]?.rawValue && acceptValue(codes[0].rawValue)) return;
        }
      } catch (_) {}
      frameRef.current = requestAnimationFrame(scanFrame);
    };
    frameRef.current = requestAnimationFrame(scanFrame);
  };

  const startZxingCamera = async video => {
    const { BrowserQRCodeReader } = await import('@zxing/browser');
    const reader = new BrowserQRCodeReader();
    setEngine('zxing');
    zxingControlsRef.current = await reader.decodeFromConstraints(
      { video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false },
      video,
      result => {
        const value = result?.getText?.() || result?.text || '';
        if (value) acceptValue(value);
      },
    );
  };

  const startCamera = async () => {
    stopCamera();
    handledRef.current = false;
    setError('');
    setOpen(true);

    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Camera access is not available here. Paste the secure handoff link below instead.');
      return;
    }

    try {
      const video = await waitForVideo(videoRef);
      if ('BarcodeDetector' in window) await startNativeCamera(video);
      else await startZxingCamera(video);
    } catch (err) {
      stopCamera();
      setOpen(true);
      setError(cameraErrorMessage(err));
    }
  };

  const useManual = () => {
    handledRef.current = false;
    if (!acceptValue(manual)) return;
    setManual('');
  };

  return (
    <div className="tracking-scanner">
      <button
        type="button"
        className={`tracking-btn ${open ? 'tracking-btn-primary' : 'tracking-btn-secondary'}`}
        onClick={open ? stopCamera : startCamera}
      >
        {open ? 'Close Handoff Scanner' : 'Scan Handoff QR'}
      </button>

      {open && (
        <div className="tracking-scanner-panel" style={{ marginTop: 10 }}>
          {!error && <video ref={videoRef} className="tracking-scanner-video" playsInline muted />}
          {engine === 'zxing' && !error && <div style={{ fontSize: 12, opacity: .68, marginTop: 6 }}>iPhone-compatible QR decoder active</div>}
          {error && <div className="tracking-warning">{error}</div>}
          <div style={{ marginTop: 8, fontSize: 12, opacity: .72 }}>
            Scan the secure QR displayed by the member handing you the gear. Line Medic will open the transfer confirmation directly inside the app.
          </div>
          <div className="tracking-manual-row">
            <input
              value={manual}
              onChange={event => setManual(event.target.value)}
              placeholder="Or paste handoff link"
              autoCapitalize="none"
              autoCorrect="off"
            />
            <button type="button" className="tracking-btn" onClick={useManual} disabled={!manual.trim()}>
              Open
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
